import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, Subscription, fromEvent, merge } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { AuthService } from './auth.service';
import { AuthSessionTerminatorService } from './auth-session-terminator.service';
import { TokenRefreshCoordinator } from './token-refresh-coordinator.service';
import { UserActivityService } from './user-activity.service';

/**
 * Browser-side idle logout.
 *
 * Watches mouse, keyboard, touch and scroll activity and logs the user out after the idle timeout the
 * server reported for this session (see AuthService.sessionPolicy$). The server enforces the same
 * window on UserSession.LastActivityAt; this timer exists so an unattended open tab is logged out
 * promptly instead of on its next request.
 *
 * Sessions created with Remember Me have no idle timeout: the timer is disarmed and only the
 * refresh-token lifetime bounds the login.
 */
@Injectable({
  providedIn: 'root',
})
export class SessionTimeoutService {
  private readonly DEFAULT_TIMEOUT_MINUTES = 60;
  private timeoutMinutes: number = this.DEFAULT_TIMEOUT_MINUTES;
  private rememberMe = false;
  private armed = false;
  private lastActivityTime: number = Date.now();
  private timeoutTimer: ReturnType<typeof setTimeout> | null = null;
  private activitySubscription: Subscription | null = null;
  private policySubscription: Subscription | null = null;

  private sessionExpiredSubject = new BehaviorSubject<boolean>(false);
  public sessionExpired$: Observable<boolean> = this.sessionExpiredSubject.asObservable();

  constructor(
    private authService: AuthService,
    private router: Router,
    private terminator: AuthSessionTerminatorService,
    private coordinator: TokenRefreshCoordinator,
    private userActivity: UserActivityService
  ) {
    this.startActivityTracking();

    // The policy can arrive after the timer was armed (page reload: the user is restored from cache
    // first, init() answers a moment later). Re-apply whenever it changes.
    this.policySubscription = this.authService.sessionPolicy$.subscribe((policy) => {
      if (this.armed && policy) {
        this.initialize(policy.idleTimeoutMinutes ?? this.DEFAULT_TIMEOUT_MINUTES, policy.rememberMe);
      }
    });
  }

  /**
   * Arm the idle timer from whatever the API has reported for the current session. Falls back to a
   * one-hour idle timeout without Remember Me when nothing has been reported yet.
   */
  initializeFromSession(): void {
    const policy = this.authService.getSessionPolicy();
    this.initialize(policy?.idleTimeoutMinutes ?? this.DEFAULT_TIMEOUT_MINUTES, policy?.rememberMe ?? false);
  }

  /**
   * Arm the idle timer.
   *
   * @param timeoutMinutes Idle minutes before logout
   * @param rememberMe When true the timer is disarmed - Remember Me sessions have no idle logout
   */
  initialize(timeoutMinutes: number = this.DEFAULT_TIMEOUT_MINUTES, rememberMe: boolean = false): void {
    this.armed = true;
    this.startActivityTracking();
    this.timeoutMinutes = timeoutMinutes > 0 ? timeoutMinutes : this.DEFAULT_TIMEOUT_MINUTES;
    this.rememberMe = rememberMe;
    this.lastActivityTime = Date.now();

    // The heartbeat only helps if it speaks before the window closes, so it follows the policy the
    // server reported for this session rather than a fixed interval.
    this.userActivity.configureFor(this.timeoutMinutes, rememberMe);

    if (rememberMe) {
      this.clearTimeout();
      console.log('[SessionTimeoutService] Remember Me session - idle logout disabled');
      return;
    }

    console.log(`[SessionTimeoutService] Idle logout armed: ${this.timeoutMinutes} minutes`);
    this.resetTimeout();
  }

  /**
   * Start tracking user activity. Idempotent: destroy() tears the subscription down on logout, and the
   * next login has to bring it back or the timer would fire regardless of activity.
   */
  private startActivityTracking(): void {
    if (this.activitySubscription) {
      return;
    }

    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click', 'keydown'];
    const allEvents$ = merge(...events.map((event) => fromEvent(document, event)));

    this.activitySubscription = allEvents$
      .pipe(debounceTime(1000))
      .subscribe(() => this.onActivity());
  }

  private onActivity(): void {
    // Deliberately does not record input. UserActivityService is the one writer of "when did the
    // user last do something", and it listens to real input only. This method is reached from
    // listeners that include mousemove and scroll, and from reset() on every successful HTTP
    // response — counting either as input would let a page that polls hold its own session open,
    // which is precisely what the shared value exists to stop. Re-arm from the shared value instead.
    this.lastActivityTime = this.userActivity.getLastInputAt();
    if (this.armed && !this.rememberMe) {
      this.resetTimeout();
    }
  }

  /**
   * Arm the timer to fire when the *shared* last input would age out.
   *
   * Sharing is the whole point. Per-tab timers signed people out of the tab they were not looking
   * at: a second monitor left on the dashboard saw no mouse or key for an hour and ended a session
   * being actively used next door.
   */
  private resetTimeout(): void {
    this.clearTimeout();
    const timeoutMs = this.timeoutMinutes * 60 * 1000;
    const elapsed = Math.max(Date.now() - (this.userActivity.getLastInputAt() || Date.now()), 0);
    const remaining = Math.max(timeoutMs - elapsed, 0);
    this.timeoutTimer = setTimeout(() => this.handleSessionTimeout(), remaining);
  }

  private clearTimeout(): void {
    if (this.timeoutTimer) {
      clearTimeout(this.timeoutTimer);
      this.timeoutTimer = null;
    }
  }

  /**
   * Idle limit reached: revoke the session server-side and go to the login page.
   */
  private handleSessionTimeout(): void {
    // Ask again before acting. This tab may have been suspended, or another may have seen input we
    // have not been told about yet; either way the shared value is the authority, not this timer.
    const lastInput = this.userActivity.getLastInputAt();
    const idleFor = Date.now() - lastInput;
    if (lastInput > 0 && idleFor < this.timeoutMinutes * 60 * 1000) {
      this.resetTimeout();
      return;
    }

    console.log('[SessionTimeoutService] Session expired due to inactivity');

    this.sessionExpiredSubject.next(true);
    this.clearTimeout();

    // One place ends sessions. This used to log out and navigate on its own, which meant that an
    // idle timeout racing a 401 produced two logouts and two navigations.
    this.terminator.end('idle-timeout', this.coordinator.currentSessionId(), { sessionExpired: true });
  }

  /**
   * Re-arm the idle timer from the shared last-input time (called after successful API calls).
   * A response is not evidence that anyone is there, so this records nothing; it only recomputes.
   */
  reset(): void {
    this.onActivity();
  }

  /**
   * Disarm on logout. Activity tracking is re-established by the next initialize().
   */
  destroy(): void {
    this.armed = false;
    this.clearTimeout();
    if (this.activitySubscription) {
      this.activitySubscription.unsubscribe();
      this.activitySubscription = null;
    }
  }
}
